import UserOutlined from '@ant-design/icons/UserOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Progress from 'antd/es/progress';
import Typography from 'antd/es/typography';
import * as jose from 'jose';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { type RegisterRequest } from '@mai/types';

import UnauthCard from '@components/UnauthCard';
import { apiClient } from '@queries/axios';

type RegisterFormValues = {
  fullName: string;
  password: string;
  confirmPassword: string;
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const token = searchParams.get('token');
  const [form] = Form.useForm<RegisterFormValues>();

  const formPasswordValue = Form.useWatch('password', form);

  const { email, isTokenExpired } = useMemo(() => {
    if (!token) {
      return { email: null, isTokenExpired: false };
    }

    const decoded = jose.decodeJwt(token);
    if (!decoded || typeof decoded === 'string') {
      return { email: null, isTokenExpired: false };
    }

    let isTokenExpired = false;
    const exp = decoded.exp;
    if (exp && typeof exp === 'number') {
      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime >= exp) {
        isTokenExpired = true;
      }
    }

    const email = decoded.email;
    if (!email || typeof email !== 'string') {
      return { email: null, isTokenExpired };
    }

    return { email, isTokenExpired };
  }, [token]);

  if (isTokenExpired) {
    return (
      <Container>
        <UnauthCard>
          <ContentContainer>
            <Typography.Title level={2}>
              Registration Link Expired
            </Typography.Title>
            <Typography.Text>
              Your registration link has expired. Please request a new one.
            </Typography.Text>
            <Button
              type="primary"
              onClick={() => navigate(`/auth/login?email=${email}`)}
            >
              Return to Sign In
            </Button>
          </ContentContainer>
        </UnauthCard>
      </Container>
    );
  }

  const onSubmit = async (values: RegisterFormValues) => {
    if (!token || !email) {
      return;
    }

    const loadingMessage = message.loading('Registering...');
    try {
      await apiClient.post('/authentication/email/register', {
        email,
        fullName: values.fullName,
        nickname: null,
        password: values.password,
        token,
      } satisfies RegisterRequest);
      loadingMessage();
      navigate('/');
    } catch (error) {
      loadingMessage();
      void message.error('An error occurred while registering');
    }
  };

  return (
    <Container>
      <UnauthCard>
        <ContentContainer>
          <Form
            form={form}
            onFinish={onSubmit}
            layout="vertical"
            style={{ width: '100%' }}
          >
            <Form.Item name="email" label="Email" initialValue={email}>
              <Input disabled />
            </Form.Item>
            <Form.Item
              name="fullName"
              label="Full Name"
              initialValue={''}
              rules={[{ required: true }]}
            >
              <Input placeholder="Enter your full name" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              initialValue={''}
              rules={[
                { required: true, message: 'Please input your password!' },
                {
                  min: 8,
                  message: 'Password must be at least 8 characters long',
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input.Password placeholder="Enter your password" />
            </Form.Item>
            <Progress
              percent={Math.min(
                ((formPasswordValue?.length ?? 0) / 16) * 100,
                100,
              )}
              status={
                ((formPasswordValue?.length ?? 0) >= 12 && 'success') ||
                ((formPasswordValue?.length ?? 0) >= 8 && 'active') ||
                'exception'
              }
              showInfo={false}
              style={{ marginBottom: '8px' }}
            />
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              initialValue={''}
              dependencies={['password']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('The passwords do not match!'),
                    );
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm your password" />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: '100%' }}
                icon={<UserOutlined />}
              >
                Register
              </Button>
            </Form.Item>
          </Form>
        </ContentContainer>
      </UnauthCard>
    </Container>
  );
};

export default RegisterPage;
